import * as React from "react"
import { MagnifyingGlass, X, Check, CaretDown } from "@phosphor-icons/react"
import { cn } from "../../../lib/utils"
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandLoading,
} from "../command"
import { useAsyncSearch } from "./use-async-search"
import type { AsyncSearchProps } from "./types"

/**
 * AsyncSearch - A unified async search/select component.
 *
 * Supports two data modes:
 * - Collection mode: Pass `collection` prop for async API search
 * - Static mode: Pass `options` prop for client-side filtering
 *
 * Supports two trigger modes:
 * - Input mode (default): Search input field like a combobox
 * - Button mode: Dropdown button like a select
 *
 * Uses inline dropdown (no portals) for reliable behavior inside modals.
 *
 * @example
 * // Async API search with input trigger
 * <AsyncSearch
 *   collection="contacts"
 *   value={contactId}
 *   onChange={(contact, id) => setContactId(id)}
 * />
 *
 * @example
 * // Static options with button trigger
 * <AsyncSearch
 *   options={templates}
 *   trigger="button"
 *   value={templateId}
 *   onChange={(template, id) => setTemplate(template)}
 *   renderItem={(t) => <div>{t.name}</div>}
 * />
 */
export function AsyncSearch<T = any>({
  // Data source
  collection,
  options,
  isLoading: externalLoading,

  // Value
  value,
  onChange,
  filter,
  sort,
  getItemId = (item: any) => item._id,
  getItemLabel = (item: any) => item.name || item.title || item.display_name || "",

  // Display
  trigger = "input",
  label,
  placeholder = "Search...",
  description,

  // Rendering
  renderItem,
  renderSelected,
  renderTriggerIcon,

  // Behavior
  clearable = true,
  disabled = false,
  menuPlacement = "bottom",
  onBlur,
  limit = 20,
  className,
  addNull,
}: AsyncSearchProps<T>) {
  const [open, setOpen] = React.useState(false)
  const [inputValue, setInputValue] = React.useState("")
  const [isFocused, setIsFocused] = React.useState(false)
  const [selectedItem, setSelectedItem] = React.useState<T | null>(null)
  const containerRef = React.useRef<HTMLDivElement>(null)
  const inputRef = React.useRef<HTMLInputElement>(null)

  const { items, isLoading, search, loadInitial } = useAsyncSearch<T>({
    collection,
    options,
    isLoading: externalLoading,
    filter,
    sort,
    limit,
    getItemId,
    getItemLabel,
    addNull,
  })

  // Find selected item in current results or use cached
  const displayItem = React.useMemo(() => {
    if (!value) return null
    const found = items.find((item) => getItemId(item) === value)
    if (found) return found
    return selectedItem
  }, [value, items, selectedItem, getItemId])

  // Update cached selected item when found in results
  React.useEffect(() => {
    if (value && items.length > 0 && !selectedItem) {
      const found = items.find((item) => getItemId(item) === value)
      if (found) {
        setSelectedItem(found)
      }
    }
  }, [value, items, selectedItem, getItemId])

  // Close on click outside
  React.useEffect(() => {
    if (!open) return

    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
        setIsFocused(false)
        setInputValue("")
        onBlur?.()
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [open, onBlur])

  // Handle input change with search
  const handleInputChange = React.useCallback(
    (newValue: string) => {
      setInputValue(newValue)
      setIsFocused(true)
      if (!open) {
        setOpen(true)
        loadInitial()
      }
      search(newValue)
    },
    [search, open, loadInitial]
  )

  // Handle option selection
  const handleSelect = React.useCallback(
    (item: T) => {
      const id = getItemId(item)
      // Handle null option
      if (id === null || id === "") {
        setSelectedItem(null)
        onChange(null, null)
      } else {
        setSelectedItem(item)
        onChange(item, id)
      }
      setOpen(false)
      setIsFocused(false)
      setInputValue("")
      inputRef.current?.blur()
    },
    [onChange, getItemId]
  )

  // Handle clear
  const handleClear = React.useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      setSelectedItem(null)
      onChange(null, null)
    },
    [onChange]
  )

  // Handle focus/open
  const handleOpen = React.useCallback(() => {
    if (disabled) return
    setIsFocused(true)
    setOpen(true)
    loadInitial()
  }, [loadInitial, disabled])

  // Handle keyboard
  const handleKeyDown = React.useCallback((e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      setOpen(false)
      setIsFocused(false)
      setInputValue("")
      inputRef.current?.blur()
    }
  }, [])

  // Get display value for input trigger mode
  const getDisplayValue = React.useCallback((): string => {
    if (!value || !displayItem) return ""
    if (renderSelected) {
      const rendered = renderSelected(displayItem)
      if (typeof rendered === "string") return rendered
      return getItemLabel(displayItem)
    }
    return getItemLabel(displayItem)
  }, [value, displayItem, renderSelected, getItemLabel])

  // Determine what to show in input (trigger="input" mode)
  const inputDisplayValue = isFocused ? inputValue : getDisplayValue()

  // Dropdown position class
  const dropdownPosition = menuPlacement === "top" ? "bottom-full mb-1" : "top-full mt-1"

  // Render the selected value display
  const renderSelectedDisplay = () => {
    if (!displayItem) {
      return <span className="text-muted-foreground">{placeholder}</span>
    }

    const icon = renderTriggerIcon?.()

    if (renderSelected) {
      const rendered = renderSelected(displayItem)
      return (
        <div className="flex items-center gap-2 truncate">
          {icon && <span className="flex-shrink-0 text-muted-foreground">{icon}</span>}
          <span className="truncate">{rendered}</span>
        </div>
      )
    }

    return (
      <div className="flex items-center gap-2 truncate">
        {icon && <span className="flex-shrink-0 text-muted-foreground">{icon}</span>}
        <span className="truncate">{getItemLabel(displayItem)}</span>
      </div>
    )
  }

  // Render dropdown item
  const renderDropdownItem = (item: T) => {
    const id = getItemId(item)
    const isSelected = id === value

    return (
      <CommandItem
        key={id || "null"}
        value={id}
        onSelect={() => handleSelect(item)}
        className={cn("cursor-pointer", isSelected && "bg-accent")}
      >
        <Check
          className={cn(
            "mr-2 h-4 w-4 flex-shrink-0",
            isSelected ? "opacity-100" : "opacity-0"
          )}
        />
        <div className="min-w-0 flex-1">
          {renderItem ? renderItem(item) : getItemLabel(item)}
        </div>
      </CommandItem>
    )
  }

  // Build items list with optional null option
  const displayItems = React.useMemo(() => {
    if (!addNull) return items

    const nullItem = { _id: null, name: "None" } as T
    return [nullItem, ...items]
  }, [items, addNull])

  return (
    <div ref={containerRef} className="relative space-y-1">
      {label && (
        <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
          {label}
        </label>
      )}

      <div className="relative">
        {trigger === "input" ? (
          // Input trigger mode
          <>
            <MagnifyingGlass className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              ref={inputRef}
              type="text"
              role="combobox"
              aria-expanded={open}
              aria-controls="async-search-list"
              aria-autocomplete="list"
              disabled={disabled}
              value={inputDisplayValue}
              placeholder={placeholder}
              onFocus={handleOpen}
              onChange={(e) => handleInputChange(e.target.value)}
              onKeyDown={handleKeyDown}
              className={cn(
                "flex h-9 w-full rounded-md border border-input bg-input-background pl-9 pr-8 py-2 text-sm shadow-sm",
                "placeholder:text-muted-foreground",
                "disabled:cursor-not-allowed disabled:opacity-50",
                className
              )}
            />
            {clearable && value && !disabled && (
              <button
                type="button"
                onClick={handleClear}
                onMouseDown={(e) => e.preventDefault()}
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded-sm opacity-50 hover:opacity-100"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </>
        ) : (
          // Button trigger mode
          <button
            type="button"
            role="combobox"
            aria-expanded={open}
            aria-controls="async-search-list"
            disabled={disabled}
            onClick={handleOpen}
            className={cn(
              "flex h-9 w-full items-center justify-between rounded-md border border-input bg-input-background px-3 py-2 text-sm shadow-sm",
              "placeholder:text-muted-foreground",
              "disabled:cursor-not-allowed disabled:opacity-50",
              className
            )}
          >
            {renderSelectedDisplay()}
            <div className="flex items-center gap-1">
              {clearable && value && !disabled && (
                <span
                  role="button"
                  tabIndex={0}
                  onClick={handleClear}
                  onKeyDown={(e) => e.key === "Enter" && handleClear(e as any)}
                  className="rounded-sm opacity-50 hover:opacity-100"
                >
                  <X className="h-3.5 w-3.5" />
                </span>
              )}
              <CaretDown className="h-4 w-4 opacity-50" />
            </div>
          </button>
        )}

        {/* Inline dropdown - no portal for modal compatibility */}
        {open && (
          <div
            className={cn(
              "absolute left-0 right-0 z-[9999] max-h-[300px] overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-lg",
              dropdownPosition
            )}
            onMouseDown={(e) => {
              // Allow focus on inputs, prevent blur for other clicks
              const target = e.target as HTMLElement
              const isInputArea = target.tagName === "INPUT" || target.closest("[cmdk-input-wrapper]")
              if (!isInputArea) {
                e.preventDefault()
              }
            }}
          >
            <Command shouldFilter={false}>
              {trigger === "button" && (
                <CommandInput
                  placeholder="Search..."
                  value={inputValue}
                  onValueChange={handleInputChange}
                />
              )}
              <CommandList id="async-search-list" className="max-h-[250px]">
                {isLoading && <CommandLoading>Searching...</CommandLoading>}
                <CommandEmpty>No results found.</CommandEmpty>
                <CommandGroup>
                  {displayItems.map((item) => renderDropdownItem(item))}
                </CommandGroup>
              </CommandList>
            </Command>
          </div>
        )}
      </div>

      {description && (
        <p className="text-xs text-muted-foreground">{description}</p>
      )}
    </div>
  )
}
